import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { Btn, Empty, Badge } from "../components/UI";
import { SlidePanel, ConfirmDialog, ReadOnlyChip } from "../components/SlidePanel";
import { FLabel, FInput, FSelect, FTextarea, SelectWithCustom } from "./engagement/FormControls";
import { MedicationPicker, resolveLibrary } from "../components/MedicationPicker";
import { MED_ROUTES } from "../lib/medVocab";
import { ClientFormsPanel } from "../components/ClientFormsPanel";
import { DateField } from "./engagement/DateField";
import { usePrograms } from "../lib/usePrograms";
import { Plus, Pencil, Trash2, Pill, HeartPulse, BedDouble, CheckCircle2, AlertTriangle, XCircle, Archive, RotateCcw, Ban, GraduationCap } from "lucide-react";
import { DiagnosisPicker } from "../components/DiagnosisPicker";
import { ArchiveDiagnosisDialog, VoidDiagnosisDialog, DiagnosisHistoryPanel, DiagnosisListToggles, ClinicianSelect, PrincipalNote, DX_TYPES, DX_TYPE_LABELS, DX_TYPE_HINT, canRemoveDx } from "../components/DiagnosisExtras";
import { PatientEducationPanel } from "../components/education/PatientEducationPanel";
import { MedQuickTools, RefillCardAction, useRefills } from "../components/medications/MedPanelTools";
import { toast } from "sonner";

const IconBtn = ({ testId, label, onClick, danger }) => (
  <button type="button" data-testid={testId} aria-label={label} onClick={onClick}
    className={`h-8 w-8 rounded-full bg-white text-[#7B6157] flex items-center justify-center ${danger ? "hover:bg-[#FFEBEE] hover:text-[#C62828]" : "hover:bg-[#EDE7DC]"}`}>
    {danger ? <Trash2 size={13} /> : <Pencil size={13} />}
  </button>
);

function useCrud({ listUrl, createUrl, itemUrl, clientId, label }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(listUrl).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [clientId, listUrl]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (form, editId, onDone) => {
    setBusy(true);
    try {
      if (editId) await api.put(`${itemUrl}/${editId}`, form);
      else await api.post(createUrl, form);
      toast.success(`${label} ${editId ? "updated" : "added"}`);
      load(); onDone?.();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  const remove = async (id, onDone) => {
    setBusy(true);
    try {
      await api.delete(`${itemUrl}/${id}`);
      toast.success(`${label} deleted`);
      load(); onDone?.();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return { items, busy, save, remove, load };
}

function PanelShell({ testId, editable, addTestId, addLabel, onAdd, empty, count, children, extra }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        {!editable ? <ReadOnlyChip /> : <span />}
        <div className="flex flex-wrap items-center gap-2">
          {extra}
          {editable && <Btn data-testid={addTestId} variant="outline" className="rounded-lg" onClick={onAdd}><Plus size={14} /> {addLabel}</Btn>}
        </div>
      </div>
      {count === 0 ? <Empty text={empty} /> : <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">{children}</div>}
    </div>
  );
}

const fmt = (d) => (d ? dayjs(d).format("MM/DD/YYYY") : "-");

/* ================= Admissions & Discharges ================= */
const LEVELS_OF_CARE = ["Outpatient (ASAM 1.0)", "Intensive Outpatient (ASAM 2.1)", "Partial Hospitalization (ASAM 2.5)", "Residential (ASAM 3.5)", "Inpatient (ASAM 4.0)"];
const DISCHARGE_TYPES = ["Treatment completed", "Transferred to higher level of care", "Transferred to lower level of care", "Against medical advice (AMA)", "Administrative discharge", "Lost to contact", "Deceased"];
const ADM_EMPTY = { admission_date: "", program: "", level_of_care: "", admitting_provider: "", reason: "", discharge_date: "", discharge_type: "", discharge_summary: "", status: "active" };

export function AdmissionsPanel({ client, onUpdated }) {
  const { user } = useAuth();
  const PROGRAMS = usePrograms();
  const editable = canEdit(user, "clinical");
  const { items, busy, save, remove } = useCrud({ listUrl: `/clients/${client.id}/admissions`, createUrl: `/clients/${client.id}/admissions`, itemUrl: "/admissions", clientId: client.id, label: "Admission" });
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState(ADM_EMPTY);
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const refreshClient = () => onUpdated && api.get(`/clients/${client.id}`).then((r) => onUpdated(r.data)).catch(() => {});

  return (
    <>
      <PanelShell testId="admissions-panel" editable={editable} addTestId="add-admission-btn" addLabel="Add Admission"
        onAdd={() => { setForm({ ...ADM_EMPTY, admission_date: dayjs().format("YYYY-MM-DD") }); setEditId(null); setPanel(true); }}
        empty="No admissions on file" count={items.length}>
        {items.map((a) => (
          <div key={a.id} data-testid={`admission-card-${a.id}`} className="rounded-2xl bg-[#F9F5EE] border border-[#F1E8DA] p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 font-heading font-bold text-[#3E2723]"><BedDouble size={15} className="text-[#7B6157]" /> {fmt(a.admission_date)}</div>
              <Badge value={a.status} />
            </div>
            <div className="mt-1.5 text-sm text-[#6D4C41]">{a.program || "-"}{a.level_of_care ? ` · ${a.level_of_care}` : ""}</div>
            <div className="mt-1 text-xs text-[#7B6157]">Admitting provider: {a.admitting_provider || "-"}</div>
            {a.reason && <div className="mt-1 text-xs text-[#7B6157]">Reason: {a.reason}</div>}
            {a.status === "discharged" && (
              <div className="mt-2 rounded-lg bg-white/70 px-2.5 py-1.5 text-xs text-[#6D4C41]">
                Discharged {fmt(a.discharge_date)} · {a.discharge_type || "-"}{a.discharge_summary ? ` - ${a.discharge_summary}` : ""}
              </div>
            )}
            {editable && (
              <div className="mt-2 flex gap-1.5 justify-end">
                <IconBtn testId={`edit-admission-${a.id}`} label="Edit" onClick={() => { setForm({ ...ADM_EMPTY, ...a }); setEditId(a.id); setPanel(true); }} />
                <IconBtn testId={`delete-admission-${a.id}`} label="Delete" danger onClick={() => setDel(a)} />
              </div>
            )}
          </div>
        ))}
      </PanelShell>

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? "Edit Admission" : "Add Admission"} subtitle={`${client.first_name} ${client.last_name} - admissions & discharges`} testId="admission-slide-panel">
        <form onSubmit={(e) => { e.preventDefault(); save(form, editId, () => { setPanel(false); refreshClient(); }); }} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div><FLabel>Admission Date *</FLabel><DateField testId="admission-date-input" value={form.admission_date} onChange={(v) => setForm((s) => ({ ...s, admission_date: v }))} /></div>
            <div><FLabel>Program</FLabel><SelectWithCustom testId="admission-program-input" value={form.program} onChange={(v) => setForm((s) => ({ ...s, program: v }))} options={PROGRAMS} customPlaceholder="Enter program" /></div>
            <div><FLabel>Level of Care</FLabel><FSelect data-testid="admission-loc-input" value={form.level_of_care} onChange={f("level_of_care")} placeholder="Select level">{LEVELS_OF_CARE.map((l) => <option key={l}>{l}</option>)}</FSelect></div>
            <div><FLabel>Admitting Provider</FLabel><FInput data-testid="admission-provider-input" value={form.admitting_provider} onChange={f("admitting_provider")} /></div>
            <div className="col-span-2"><FLabel>Reason for Admission</FLabel><FInput data-testid="admission-reason-input" value={form.reason} onChange={f("reason")} /></div>
            <div><FLabel>Status</FLabel>
              <FSelect data-testid="admission-status-input" value={form.status} onChange={f("status")}>
                <option value="active">Active</option><option value="discharged">Discharged</option>
              </FSelect></div>
          </div>
          {form.status === "discharged" && (
            <div className="rounded-xl border border-[#F1E8DA] bg-[#FDFCF8] p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><FLabel>Discharge Date</FLabel><DateField testId="discharge-date-input" value={form.discharge_date} onChange={(v) => setForm((s) => ({ ...s, discharge_date: v }))} /></div>
                <div><FLabel>Discharge Type</FLabel><FSelect data-testid="discharge-type-input" value={form.discharge_type} onChange={f("discharge_type")} placeholder="Select type">{DISCHARGE_TYPES.map((t) => <option key={t}>{t}</option>)}</FSelect></div>
              </div>
              <div><FLabel>Discharge Summary</FLabel><FTextarea data-testid="discharge-summary-input" rows={2} value={form.discharge_summary} onChange={f("discharge_summary")} /></div>
            </div>
          )}
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn data-testid="admission-save-btn" type="submit" disabled={busy || !form.admission_date}>{busy ? "Saving…" : editId ? "Save Changes" : "Add Admission"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={() => remove(del.id, () => { setDel(null); refreshClient(); })} busy={busy}
        title="Delete admission" description={`Delete the admission dated ${del && fmt(del.admission_date)} for ${client.first_name} ${client.last_name}?`} />
    </>
  );
}

/* ================= Diagnosis ================= */
const DX_STATUSES = ["Active", "In Remission", "Resolved"];
const DX_EMPTY = { code: "", description: "", dx_type: "Principal", status: "Active", onset_date: "", date_diagnosed: "", ruled_out_date: "", diagnosed_by: "", diagnosed_by_id: "", catalog_id: "", notes: "" };
const RoundBtn = ({ testId, label, onClick, danger, children }) => (
  <button type="button" data-testid={testId} aria-label={label} title={label} onClick={onClick}
    className={`h-8 w-8 rounded-full bg-white flex items-center justify-center ${danger ? "text-[#C62828] hover:bg-[#FFEBEE]" : "text-[#7B6157] hover:bg-[#EDE7DC]"}`}>{children}</button>
);

export function DiagnosisPanel({ client, onUpdated }) {
  const { user } = useAuth();
  const editable = canEdit(user, "clinical");
  const isAdmin = user?.role === "admin";
  const [showArchived, setShowArchived] = useState(false);
  const [showVoided, setShowVoided] = useState(false);
  const [voiding, setVoiding] = useState(null);
  const [education, setEducation] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const listQs = new URLSearchParams({ ...(showArchived ? { include_archived: "true" } : {}), ...(showVoided ? { include_voided: "true" } : {}) }).toString();
  const { items, busy, save, load } = useCrud({ listUrl: `/clients/${client.id}/diagnoses${listQs ? `?${listQs}` : ""}`, createUrl: `/clients/${client.id}/diagnoses`, itemUrl: "/diagnoses", clientId: client.id, label: "Diagnosis" });
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState(DX_EMPTY);
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);
  const codesLocked = !!editId && !canRemoveDx(user, form);
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const sync = () => api.get(`/clients/${client.id}`).then((r) => onUpdated?.(r.data));
  const openNew = () => {
    const mine = ["clinician", "admin"].includes(user?.role);
    setForm({ ...DX_EMPTY, date_diagnosed: dayjs().format("YYYY-MM-DD"), diagnosed_by: mine ? `${user.first_name} ${user.last_name}` : "", diagnosed_by_id: mine ? user.id : "" });
    setEditId(null); setPanel(true);
  };
  const restore = async (d) => {
    try { await api.post(`/diagnoses/${d.id}/restore`); toast.success("Diagnosis restored"); load(); sync(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <>
      <PanelShell testId="diagnosis-panel" editable={editable} addTestId="add-diagnosis-btn" addLabel="Add Diagnosis"
        extra={<DiagnosisListToggles showArchived={showArchived} setShowArchived={setShowArchived} showVoided={showVoided} setShowVoided={setShowVoided} isAdmin={isAdmin} onHistory={() => setHistoryOpen(true)} testPrefix="diagnosis" />}
        onAdd={openNew} empty="No diagnoses on file" count={items.length}>
        {items.map((d) => (
          <div key={d.id} data-testid={`diagnosis-card-${d.id}`} className={`rounded-2xl border p-4 ${d.status === "Voided" ? "bg-[#FFF8F8] border-[#FFCDD2]" : d.status === "Active" ? "bg-[#F9F5EE] border-[#F1E8DA]" : "bg-[#FAFAF7] border-[#EFEBE0] opacity-75"}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <HeartPulse size={15} className="text-[#7B6157] shrink-0" />
                {d.code && <span className="rounded-md bg-[#4E342E] px-2 py-0.5 text-xs font-bold text-white">{d.code}</span>}
                <span className="font-heading font-bold text-[#3E2723] break-words">{d.description}</span>
              </div>
            </div>
            {(d.icd9_code || d.snomed_code) && <div className="mt-1 font-mono text-[10px] text-[#7B6157]" data-testid={`diagnosis-codes-${d.id}`}>{[d.icd9_code && `ICD-9 ${d.icd9_code}`, d.snomed_code && `SNOMED ${d.snomed_code}`].filter(Boolean).join(" · ")}</div>}
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-[#7B6157]">
              <span className="font-semibold text-[#5D4037]">{DX_TYPE_LABELS[d.dx_type] || d.dx_type}</span>
              <span className={d.status === "Active" ? "font-semibold text-[#2E7D32]" : d.status === "Voided" ? "font-semibold text-[#C62828]" : ""}>{d.status}</span>
              {d.date_diagnosed && <span>Diagnosed {fmt(d.date_diagnosed)}</span>}
              {d.onset_date && <span>Onset {fmt(d.onset_date)}</span>}
              {d.ruled_out_date && <span data-testid={`diagnosis-ruled-out-${d.id}`}>Ruled out {fmt(d.ruled_out_date)}</span>}
              {d.updated_at && <span>Modified {fmt(d.updated_at)}</span>}
              {d.diagnosed_by && <span>By {d.diagnosed_by}</span>}
            </div>
            {d.notes && <p className="mt-2 text-xs text-[#7B6157] bg-white/60 rounded-lg px-2.5 py-1.5">{d.notes}</p>}
            {d.status === "Archived" && d.archive_reason && <p className="mt-2 text-[11px] text-[#546E7A]">Archived: {d.archive_reason}</p>}
            {d.status === "Voided" && <p className="mt-2 text-[11px] text-[#C62828]" data-testid={`diagnosis-void-info-${d.id}`}>Voided by {d.voided_by_name} on {fmt(d.voided_at)}: {d.void_reason}</p>}
            {d.status !== "Voided" && (
              <div className="mt-2 flex gap-1.5 justify-end">
                <RoundBtn testId={`diagnosis-education-${d.id}`} label="Patient Education" onClick={() => setEducation(d)}><GraduationCap size={13} /></RoundBtn>
                {editable && (d.status === "Archived" ? (
                  canRemoveDx(user, d) && <button type="button" data-testid={`restore-diagnosis-${d.id}`} onClick={() => restore(d)} className="flex h-8 items-center gap-1 rounded-full bg-white px-3 text-xs font-semibold text-[#4E342E] hover:bg-[#EDE7DC]"><RotateCcw size={12} /> Restore</button>
                ) : (
                  <>
                    <IconBtn testId={`edit-diagnosis-${d.id}`} label="Edit" onClick={() => { setForm({ ...DX_EMPTY, ...d }); setEditId(d.id); setPanel(true); }} />
                    {canRemoveDx(user, d) && <RoundBtn testId={`delete-diagnosis-${d.id}`} label="Archive" onClick={() => setDel(d)}><Archive size={13} /></RoundBtn>}
                  </>
                ))}
                {editable && canRemoveDx(user, d) && <RoundBtn testId={`void-diagnosis-${d.id}`} label="Void, entered in error" danger onClick={() => setVoiding(d)}><Ban size={13} /></RoundBtn>}
              </div>
            )}
          </div>
        ))}
      </PanelShell>

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? "Edit Diagnosis" : "Add Diagnosis"} subtitle={`${client.first_name} ${client.last_name} - diagnostic record`} testId="diagnosis-slide-panel">
        <form onSubmit={(e) => { e.preventDefault(); save({ ...form, icd10_code: form.code }, editId, () => { setPanel(false); sync(); }); }} className="space-y-5">
          {codesLocked ? (
            <p className="rounded-xl bg-[#F5F2EB] px-3 py-2 text-xs text-[#6D4C41]" data-testid="diagnosis-codes-locked">Only {form.created_by_name || form.diagnosed_by || "the clinician who added it"} or an admin can change the code or description. You can still update the type, status, dates, and notes.</p>
          ) : (
          <div className="rounded-xl border border-[#EFEBE0] bg-[#F9F5EE] p-3">
            <FLabel>Find in Diagnosis Library</FLabel>
            <DiagnosisPicker testId="diagnosis-dx-picker" onPick={(row) => setForm((s) => ({ ...s, catalog_id: row.id, code: row.code, description: row.title, icd9_code: row.icd9 || "", snomed_code: row.snomed || "" }))} />
            {form.catalog_id && <p className="mt-1.5 text-[11px] text-[#2E7D32]" data-testid="diagnosis-dx-linked">Linked to library entry {form.code}</p>}
          </div>
          )}
          <div><FLabel>Diagnosis *</FLabel>
            <FInput data-testid="diagnosis-desc-input" disabled={codesLocked} value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value, catalog_id: "" }))} placeholder="Pick from the library above, or type a description" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><FLabel>ICD-10 Code</FLabel><FInput data-testid="diagnosis-code-input" disabled={codesLocked} value={form.code} onChange={(e) => setForm((s) => ({ ...s, code: e.target.value, catalog_id: "" }))} placeholder="F41.1" /></div>
            <div><FLabel>ICD-9 Code</FLabel><FInput data-testid="diagnosis-icd9-input" disabled={codesLocked} value={form.icd9_code || ""} onChange={f("icd9_code")} placeholder="300.02" /></div>
            <div><FLabel>SNOMED Code</FLabel><FInput data-testid="diagnosis-snomed-input" disabled={codesLocked} value={form.snomed_code || ""} onChange={f("snomed_code")} placeholder="21897009" /></div>
            <div><FLabel>Type</FLabel><FSelect data-testid="diagnosis-type-input" value={form.dx_type} onChange={f("dx_type")}>{DX_TYPES.map((t) => <option key={t} value={t}>{DX_TYPE_LABELS[t]}</option>)}</FSelect>
              {DX_TYPE_HINT[form.dx_type] && <p className="mt-1 text-[11px] text-[#7B6157]" data-testid="diagnosis-type-hint">{DX_TYPE_HINT[form.dx_type]}</p>}
              <PrincipalNote items={items} form={form} editId={editId} testId="diagnosis-principal-note" /></div>
            {form.dx_type === "Rule-out" && <div><FLabel>Date Ruled Out</FLabel><DateField testId="diagnosis-ruled-out-input" value={form.ruled_out_date} onChange={(v) => setForm((s) => ({ ...s, ruled_out_date: v }))} /></div>}
            <div><FLabel>Status</FLabel><FSelect data-testid="diagnosis-status-input" value={form.status} onChange={f("status")}>{DX_STATUSES.map((s) => <option key={s}>{s}</option>)}</FSelect></div>
            <div><FLabel>Date of Diagnosis</FLabel><DateField testId="diagnosis-date-input" value={form.date_diagnosed} onChange={(v) => setForm((s) => ({ ...s, date_diagnosed: v }))} /></div>
            <div><FLabel>Onset Date</FLabel><DateField testId="diagnosis-onset-input" value={form.onset_date} onChange={(v) => setForm((s) => ({ ...s, onset_date: v }))} /></div>
            <div><FLabel>Diagnosing Clinician</FLabel><ClinicianSelect testId="diagnosis-by-input" value={form.diagnosed_by} valueId={form.diagnosed_by_id}
              onChange={({ name, id }) => setForm((s) => ({ ...s, diagnosed_by: name, diagnosed_by_id: id }))} /></div>
          </div>
          <div><FLabel>Notes / Specifiers</FLabel><FTextarea data-testid="diagnosis-notes-input" rows={2} value={form.notes} onChange={f("notes")} /></div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn data-testid="diagnosis-save-btn" type="submit" disabled={busy || !form.description.trim()}>{busy ? "Saving…" : editId ? "Save Changes" : "Add Diagnosis"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <ArchiveDiagnosisDialog dx={del} onClose={() => setDel(null)} onDone={() => { setDel(null); load(); sync(); }} />
      <VoidDiagnosisDialog dx={voiding} onClose={() => setVoiding(null)} onDone={() => { setVoiding(null); load(); sync(); }} />
      <PatientEducationPanel dx={education} client={client} onClose={() => setEducation(null)} />
      <DiagnosisHistoryPanel client={client} open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </>
  );
}

/* ================= Medications ================= */
const ROUTES = MED_ROUTES;
const MED_FREQS = ["Daily", "Twice daily (BID)", "Three times daily (TID)", "Every morning", "Every evening", "Weekly", "As needed (PRN)"];
const MED_STATUSES = ["Active", "Discontinued", "Completed", "On hold"];
const MED_EMPTY = { medication: "", strength: "", form: "", catalog_id: "", presentation_id: "", dosage: "", frequency: "Daily", route: "Oral (PO)", prescriber: "", start_date: "", end_date: "", status: "Active", purpose: "", notes: "" };

export function MedicationsPanel({ client, onUpdated, onOpenMasterList }) {
  const { user } = useAuth();
  const editable = canEdit(user, "clinical");
  const refills = useRefills(client);
  const { items, busy, save, remove } = useCrud({ listUrl: `/clients/${client.id}/medications`, createUrl: `/clients/${client.id}/medications`, itemUrl: "/medications", clientId: client.id, label: "Medication" });
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState(MED_EMPTY);
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const sync = () => api.get(`/clients/${client.id}`).then((r) => onUpdated?.(r.data));

  return (
    <>
      <ClientFormsPanel client={client} category="Orders" match="medication|prescri|pharmac|med " title="Medication Orders" emptyHide />
      <MedQuickTools client={client} onOpenMasterList={onOpenMasterList} />
      <PanelShell testId="medications-panel" editable={editable} addTestId="add-medication-btn" addLabel="Add Medication"
        onAdd={() => { setForm({ ...MED_EMPTY, start_date: dayjs().format("YYYY-MM-DD") }); setEditId(null); setPanel(true); }}
        empty="No medications on file" count={items.length}>
        {items.map((m) => (
          <div key={m.id} data-testid={`medication-card-${m.id}`} className={`rounded-2xl border p-4 ${m.status === "Active" ? "bg-[#F9F5EE] border-[#F1E8DA]" : "bg-[#FAFAF7] border-[#EFEBE0] opacity-75"}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Pill size={15} className="text-[#7B6157] shrink-0" />
                <span className="font-heading font-bold text-[#3E2723] break-words">{m.medication}{(m.strength || m.dosage) ? ` ${m.strength || m.dosage}` : ""}</span>
              </div>
              <Badge value={m.status?.toLowerCase() === "active" ? "active" : m.status} />
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-[#7B6157]">
              <span>{m.frequency}</span><span>{m.route}</span>{m.form && <span>{m.form}</span>}{m.strength && m.dosage && m.dosage !== m.strength && <span>Dose: {m.dosage}</span>}
              {m.prescriber && <span>Rx: {m.prescriber}</span>}
              {m.start_date && <span>Since {fmt(m.start_date)}</span>}
              {m.end_date && <span>Until {fmt(m.end_date)}</span>}
            </div>
            {m.purpose && <div className="mt-1 text-xs text-[#7B6157]">For: {m.purpose}</div>}
            {m.notes && <p className="mt-2 text-xs text-[#7B6157] bg-white/60 rounded-lg px-2.5 py-1.5">{m.notes}</p>}
            <div className="mt-2 flex items-center justify-end gap-1.5">
              <RefillCardAction m={m} pending={refills.pending[m.id]} canLog={editable} onOpen={refills.open} />
              {editable && <>
                <IconBtn testId={`edit-medication-${m.id}`} label="Edit" onClick={() => { setForm({ ...MED_EMPTY, ...m }); setEditId(m.id); setPanel(true); }} />
                <IconBtn testId={`delete-medication-${m.id}`} label="Delete" danger onClick={() => setDel(m)} />
              </>}
            </div>
          </div>
        ))}
      </PanelShell>

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? "Edit Medication" : "Add Medication"} subtitle={`${client.first_name} ${client.last_name} - medication record`} testId="medication-slide-panel">
        <form onSubmit={async (e) => { e.preventDefault(); try { const payload = await resolveLibrary(form); save(payload, editId, () => { setPanel(false); sync(); }); } catch (err) { toast.error(apiError(err)); } }} className="space-y-5">
          <div><FLabel>Medication and strength *</FLabel>
            <MedicationPicker testId="medication-name-input" value={form}
              onChange={(patch) => setForm((s) => ({ ...s, ...patch, purpose: s.purpose }))} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><FLabel>Dose (for example 1 tablet)</FLabel><FInput data-testid="medication-dosage-input" value={form.dosage} onChange={f("dosage")} placeholder="1 tablet" /></div>
            <div><FLabel>Frequency</FLabel><FSelect data-testid="medication-frequency-input" value={form.frequency} onChange={f("frequency")}>{MED_FREQS.map((x) => <option key={x}>{x}</option>)}</FSelect></div>
            <div><FLabel>Route</FLabel><FSelect data-testid="medication-route-input" value={form.route} onChange={f("route")}>{ROUTES.map((r) => <option key={r}>{r}</option>)}</FSelect></div>
            <div><FLabel>Status</FLabel><FSelect data-testid="medication-status-input" value={form.status} onChange={f("status")}>{MED_STATUSES.map((s) => <option key={s}>{s}</option>)}</FSelect></div>
            <div><FLabel>Start Date</FLabel><DateField testId="medication-start-input" value={form.start_date} onChange={(v) => setForm((s) => ({ ...s, start_date: v }))} /></div>
            <div><FLabel>End Date</FLabel><DateField testId="medication-end-input" value={form.end_date} onChange={(v) => setForm((s) => ({ ...s, end_date: v }))} /></div>
            <div><FLabel>Prescriber</FLabel><FInput data-testid="medication-prescriber-input" value={form.prescriber} onChange={f("prescriber")} /></div>
            <div><FLabel>Purpose / Indication</FLabel><FInput data-testid="medication-purpose-input" value={form.purpose} onChange={f("purpose")} /></div>
          </div>
          <div><FLabel>Notes</FLabel><FTextarea data-testid="medication-notes-input" rows={2} value={form.notes} onChange={f("notes")} /></div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn data-testid="medication-save-btn" type="submit" disabled={busy || !form.medication.trim()}>{busy ? "Saving…" : editId ? "Save Changes" : "Add Medication"}</Btn>
          </div>
        </form>
      </SlidePanel>

      {refills.dialog}
      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={() => remove(del.id, () => { setDel(null); sync(); })} busy={busy}
        title="Delete medication" description={`Delete "${del?.medication}${del?.dosage ? ` ${del.dosage}` : ""}" from ${client.first_name}'s medication list?`} />
    </>
  );
}

/* ================= Compliance & Tracking ================= */
const CHECK_ICONS = {
  ok: [CheckCircle2, "text-[#2E7D32] bg-[#E8F5E9]"],
  warn: [AlertTriangle, "text-[#876600] bg-[#FFF8E1]"],
  fail: [XCircle, "text-[#C62828] bg-[#FFEBEE]"],
};

export function CompliancePanel({ client }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/clients/${client.id}/compliance`).then((r) => setData(r.data)).catch(() => setData({ score: 0, checks: [] }));
  }, [client.id]);
  if (!data) return <div className="py-10 text-center text-sm text-[#84675B]">Checking compliance…</div>;
  const scoreColor = data.score >= 80 ? "text-[#2E7D32]" : data.score >= 50 ? "text-[#876600]" : "text-[#C62828]";
  return (
    <div data-testid="compliance-panel">
      <div className="mb-5 flex items-center gap-4">
        <div className={`font-heading text-4xl font-bold ${scoreColor}`} data-testid="compliance-score">{data.score}%</div>
        <div className="text-sm text-[#6D4C41]">Chart compliance score<br /><span className="text-xs text-[#84675B]">Auto-computed from consents, treatment plan, authorizations, documentation, intake, allergies & diagnosis</span></div>
      </div>
      <div className="space-y-2">
        {data.checks.map((c) => {
          const [Icon, cls] = CHECK_ICONS[c.status] || CHECK_ICONS.warn;
          return (
            <div key={c.key} data-testid={`compliance-check-${c.key}`} className="flex items-center gap-3 rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] px-4 py-3">
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${cls}`}><Icon size={16} /></span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[#3E2723]">{c.label}</div>
                <div className="text-xs text-[#7B6157]">{c.detail}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
